/**
 * Demo provider: sample London branch data plus a fake socket that streams realtime events.
 * Enabled with VITE_USE_MOCK_REALTIME=true. Never imported by the real API path.
 *
 * Categories: Cleaning, Window cleaning, Carpet & upholstery. Workers are spread across London
 * so each job has a mix of nearby candidates, out-of-range, offline, on-leave and busy workers.
 *
 * Try:
 *   BK-1046 Deep clean (Camden, unassigned, needs 2)  -> 6 nearby cleaners, Zara has a Conflict
 *   BK-1047 Window clean (no coordinates)             -> "Locate" geocodes it, then window cleaners
 *   BK-1056 Carpet clean (Wimbledon)                  -> empty state, "Increase radius" to 15 km
 *   BK-1049 Upholstery clean                          -> Aisha + Marcus assigned, Remove either
 *   BK-1042 End of tenancy (in progress)              -> Remove is blocked after start
 *
 * Scripted events (seconds after the board connects):
 *   ~20s  Noah Wilson (worker.status -> on_job): leaves the nearby strip with a fade
 *   ~50s  Noah is available again and reappears
 *   Any worker you add: server echo (job.worker.added), accepts after ~8s
 * With SHOW_WORKER_RESPONSE_STATUS=true the response demo also runs: Liam declines BK-1048 at
 * ~15s, Marcus's stale offer expires, Emma declines anything she is added to.
 */
import { realtimeConfig } from '../config'
import { schedulingRules, SHOW_WORKER_RESPONSE_STATUS } from '../schedulingRules'
import type { Job, JobWorker, RealtimeEvent, RealtimeSnapshot, Worker, WorkerJobStatus, WorkerResponse } from '../types'
import { haversineKm } from '../utils/distance'
import { applyGeocodeCache } from '../utils/geocode-cache'
import { findNearbyCandidates } from '../utils/nearby-workers'
import { addDaysToDateKey, getMinutesOfDay, toDateKey, zonedTimeToUtc } from '../utils/time'
import type { FetchSnapshotParams, JobCandidate, RealtimeConnection, RealtimeDataSource, SocketHandlers } from './data-source'
import { createAbortError } from './shared-resource'

export const MOCK_TIME_ZONE = 'Europe/London'

const TICK_MS = 1_000
const ARRIVAL_KM = 0.06
const MAX_CREATED_JOBS = 3
const DECLINE_DEMO_AFTER_TICKS = 15
const BUSY_DEMO_AFTER_TICKS = 20
const BUSY_DEMO_DURATION_TICKS = 30
const BUSY_DEMO_WORKER_ID = 'w12'

const CLEANING = 'cat-clean'
const WINDOWS = 'cat-window'
const CARPET = 'cat-carpet'

const CATEGORY_NAME: Record<string, string> = { [CLEANING]: 'Cleaning', [WINDOWS]: 'Window cleaning', [CARPET]: 'Carpet & upholstery' }

type SeedWorker = Omit<Worker, 'lastSeenAt'> & { lastSeenMinutesAgo: number }

const SEED_WORKERS: SeedWorker[] = [
  { id: 'w1', name: 'Aisha Khan', phone: '+44 7700 900101', avatarUrl: null, status: 'available', lat: 51.5152, lng: -0.1418, currentJobId: null, rating: 4.9, lastSeenMinutesAgo: 0, categories: [CLEANING, CARPET] },
  { id: 'w2', name: 'Tom Price', phone: '+44 7700 900102', avatarUrl: null, status: 'on_job', lat: 51.5010, lng: -0.0915, currentJobId: 'j2', rating: 4.6, lastSeenMinutesAgo: 0, categories: [CLEANING] },
  { id: 'w3', name: 'Priya Nair', phone: '+44 7700 900103', avatarUrl: null, status: 'on_job', lat: 51.5390, lng: -0.1426, currentJobId: 'j3', rating: 4.8, lastSeenMinutesAgo: 0, categories: [CLEANING] },
  { id: 'w4', name: 'Daniel Okafor', phone: '+44 7700 900104', avatarUrl: null, status: 'late', lat: 51.4613, lng: -0.1156, currentJobId: 'j4', rating: 4.2, lastSeenMinutesAgo: 1, categories: [CLEANING] },
  { id: 'w5', name: 'Emma Walsh', phone: '+44 7700 900105', avatarUrl: null, status: 'available', lat: 51.4946, lng: -0.1747, currentJobId: null, rating: 4.7, lastSeenMinutesAgo: 0, categories: [CLEANING, WINDOWS] },
  { id: 'w6', name: 'Liam Chen', phone: '+44 7700 900106', avatarUrl: null, status: 'available', lat: null, lng: null, currentJobId: null, rating: 4.5, lastSeenMinutesAgo: 3, categories: [CLEANING] },
  { id: 'w7', name: 'Sofia Rossi', phone: '+44 7700 900107', avatarUrl: null, status: 'on_job', lat: 51.5390, lng: -0.1033, currentJobId: 'j5', rating: 4.9, lastSeenMinutesAgo: 0, categories: [CARPET] },
  { id: 'w8', name: 'Marcus Green', phone: '+44 7700 900108', avatarUrl: null, status: 'offline', lat: 51.5465, lng: -0.2034, currentJobId: null, rating: 4.3, lastSeenMinutesAgo: 72, categories: [CLEANING, CARPET] },
  { id: 'w9', name: 'Grace Hall', phone: '+44 7700 900109', avatarUrl: null, status: 'available', lat: 51.5330, lng: -0.1500, currentJobId: null, rating: 4.4, lastSeenMinutesAgo: 1, onLeave: true, categories: [CLEANING] },
  { id: 'w10', name: 'Jack Turner', phone: '+44 7700 900110', avatarUrl: null, status: 'available', lat: 51.5154, lng: -0.1755, currentJobId: null, rating: 4.6, lastSeenMinutesAgo: 0, categories: [WINDOWS] },
  { id: 'w11', name: 'Olivia Brown', phone: '+44 7700 900111', avatarUrl: null, status: 'available', lat: 51.5412, lng: -0.1466, currentJobId: null, rating: 4.8, lastSeenMinutesAgo: 0, categories: [CLEANING] },
  { id: 'w12', name: 'Noah Wilson', phone: '+44 7700 900112', avatarUrl: null, status: 'available', lat: 51.5308, lng: -0.1238, currentJobId: null, rating: 4.5, lastSeenMinutesAgo: 0, categories: [CLEANING, CARPET] },
  { id: 'w13', name: 'Mia Patel', phone: '+44 7700 900113', avatarUrl: null, status: 'available', lat: 51.4791, lng: -0.1476, currentJobId: null, rating: 4.7, lastSeenMinutesAgo: 0, categories: [WINDOWS, CLEANING] },
  { id: 'w14', name: 'Ethan Davies', phone: '+44 7700 900114', avatarUrl: null, status: 'available', lat: 51.3762, lng: -0.0982, currentJobId: null, rating: 4.1, lastSeenMinutesAgo: 2, categories: [CLEANING] },
  { id: 'w15', name: 'Chloe Martin', phone: '+44 7700 900115', avatarUrl: null, status: 'available', lat: 51.4826, lng: -0.0077, currentJobId: null, rating: 4.6, lastSeenMinutesAgo: 0, categories: [CARPET] },
  { id: 'w16', name: 'Ryan Scott', phone: '+44 7700 900116', avatarUrl: null, status: 'available', lat: 51.5416, lng: -0.0240, currentJobId: null, rating: 4.3, lastSeenMinutesAgo: 0, categories: [CLEANING] },
  { id: 'w17', name: 'Hannah Lee', phone: '+44 7700 900117', avatarUrl: null, status: 'available', lat: 51.4613, lng: -0.3037, currentJobId: null, rating: 4.4, lastSeenMinutesAgo: 0, categories: [WINDOWS] },
  { id: 'w18', name: 'Zara Ahmed', phone: '+44 7700 900118', avatarUrl: null, status: 'available', lat: 51.5362, lng: -0.1560, currentJobId: null, rating: 4.7, lastSeenMinutesAgo: 0, categories: [CLEANING] },
]

interface SeedJobWorker {
  workerId: string
  response: WorkerResponse
  status: WorkerJobStatus
  sentMinutesAgo: number
}

interface SeedJob {
  id: string
  bookingNo: string
  service: string
  categoryId: string
  area: string
  /** null = job without coordinates (demo of the "Locate"/geocoding flow). */
  lat: number | null
  lng: number | null
  address: string | null
  /** Minutes relative to the demo anchor time. */
  startOffset: number
  durationMinutes: number
  status: Job['status']
  requiredWorkers: number
  workers: SeedJobWorker[]
}

const accepted = (workerId: string, status: WorkerJobStatus = 'assigned', sentMinutesAgo = 180): SeedJobWorker => ({ workerId, response: 'accepted', status, sentMinutesAgo })

const SEED_JOBS: SeedJob[] = [
  { id: 'j1', bookingNo: 'BK-1041', service: 'Deep clean', categoryId: CLEANING, area: 'Shoreditch', lat: 51.5265, lng: -0.0798, address: '12 Rivington St, London EC2A 3DU', startOffset: -240, durationMinutes: 120, status: 'completed', requiredWorkers: 1, workers: [accepted('w2', 'completed', 600)] },
  { id: 'j2', bookingNo: 'BK-1042', service: 'End of tenancy', categoryId: CLEANING, area: 'Southwark', lat: 51.5010, lng: -0.0915, address: '45 Borough High St, London SE1 1NP', startOffset: -60, durationMinutes: 120, status: 'in_progress', requiredWorkers: 1, workers: [accepted('w2', 'started')] },
  { id: 'j3', bookingNo: 'BK-1043', service: 'Oven clean', categoryId: CLEANING, area: 'Hackney', lat: 51.5450, lng: -0.0553, address: '210 Mare St, London E8 3RD', startOffset: 20, durationMinutes: 60, status: 'en_route', requiredWorkers: 1, workers: [accepted('w3', 'on_the_way')] },
  { id: 'j4', bookingNo: 'BK-1044', service: 'Regular clean', categoryId: CLEANING, area: 'Clapham', lat: 51.4620, lng: -0.1380, address: '8 Clapham Common South Side, London SW4 7AA', startOffset: -20, durationMinutes: 90, status: 'late', requiredWorkers: 1, workers: [accepted('w4', 'on_the_way')] },
  { id: 'j5', bookingNo: 'BK-1045', service: 'Carpet clean', categoryId: CARPET, area: 'Islington', lat: 51.5390, lng: -0.1033, address: '150 Upper St, London N1 1RA', startOffset: -30, durationMinutes: 90, status: 'in_progress', requiredWorkers: 2, workers: [accepted('w7', 'started')] },
  { id: 'j6', bookingNo: 'BK-1046', service: 'Deep clean', categoryId: CLEANING, area: 'Camden', lat: 51.5390, lng: -0.1426, address: '30 Parkway, London NW1 7AH', startOffset: 45, durationMinutes: 120, status: 'unassigned', requiredWorkers: 2, workers: [] },
  { id: 'j7', bookingNo: 'BK-1047', service: 'Window clean', categoryId: WINDOWS, area: 'Kensington', lat: null, lng: null, address: '200 Kensington High St, London W8 7RG', startOffset: 120, durationMinutes: 60, status: 'unassigned', requiredWorkers: 1, workers: [] },
  {
    id: 'j8', bookingNo: 'BK-1048', service: 'Regular clean', categoryId: CLEANING, area: 'Chelsea', lat: 51.4875, lng: -0.1687, address: "90 King's Rd, London SW3 4TZ", startOffset: 150, durationMinutes: 90, status: 'scheduled', requiredWorkers: 2,
    workers: [accepted('w5'), { workerId: 'w6', response: 'pending', status: 'assigned', sentMinutesAgo: 18 }],
  },
  {
    id: 'j9', bookingNo: 'BK-1049', service: 'Upholstery clean', categoryId: CARPET, area: 'Marylebone', lat: 51.5225, lng: -0.1545, address: '22 Marylebone High St, London W1U 4PA', startOffset: 240, durationMinutes: 90, status: 'scheduled', requiredWorkers: 2,
    workers: [accepted('w1'), { workerId: 'w8', response: 'pending', status: 'assigned', sentMinutesAgo: 34 }],
  },
  // Zara's own job overlaps BK-1046, so she shows up there with a Conflict chip.
  { id: 'j10', bookingNo: 'BK-1055', service: 'Regular clean', categoryId: CLEANING, area: 'Primrose Hill', lat: 51.5390, lng: -0.1600, address: '12 Regents Park Rd, London NW1 7TX', startOffset: 60, durationMinutes: 90, status: 'scheduled', requiredWorkers: 1, workers: [accepted('w18')] },
  // No carpet cleaner within 10 km: demo of the empty state and "Increase radius".
  { id: 'j11', bookingNo: 'BK-1056', service: 'Carpet clean', categoryId: CARPET, area: 'Wimbledon', lat: 51.4214, lng: -0.2064, address: '15 The Broadway, London SW19 1RE', startOffset: 180, durationMinutes: 90, status: 'unassigned', requiredWorkers: 1, workers: [] },
]

/** Unassigned booking from two weeks ago, to demo the "Overdue Nd" chip. */
const OVERDUE_JOB: SeedJob = {
  id: 'j0', bookingNo: 'BK-1027', service: 'Regular clean', categoryId: CLEANING, area: 'Brixton', lat: null, lng: null, address: null,
  startOffset: 0, durationMinutes: 90, status: 'unassigned', requiredWorkers: 1, workers: [],
}

const NEW_JOB_TEMPLATES = [
  { service: 'Deep clean', categoryId: CLEANING, area: 'Bloomsbury', lat: 51.5220, lng: -0.1250 },
  { service: 'Regular clean', categoryId: CLEANING, area: 'Bermondsey', lat: 51.4980, lng: -0.0640 },
  { service: 'Window clean', categoryId: WINDOWS, area: 'Notting Hill', lat: 51.5090, lng: -0.1960 },
]

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

/** Keeps demo jobs inside one calendar day regardless of when the demo runs. */
function resolveAnchorMinutes(now: number) {
  return Math.min(Math.max(Math.round(getMinutesOfDay(now, MOCK_TIME_ZONE) / 5) * 5, 10 * 60), 17 * 60 + 30)
}

function buildJob(seed: SeedJob, dateKey: string, startMinutes: number, now: number): Job {
  const start = zonedTimeToUtc(dateKey, startMinutes, MOCK_TIME_ZONE)
  return {
    id: seed.id,
    bookingNo: seed.bookingNo,
    service: seed.service,
    categoryId: seed.categoryId,
    categoryName: CATEGORY_NAME[seed.categoryId] ?? null,
    area: seed.area,
    lat: seed.lat,
    lng: seed.lng,
    address: seed.address,
    scheduledStart: start.toISOString(),
    scheduledEnd: new Date(start.getTime() + seed.durationMinutes * 60_000).toISOString(),
    status: seed.status,
    requiredWorkers: seed.requiredWorkers,
    workers: seed.workers.map((worker, index) => ({
      workerId: worker.workerId,
      response: worker.response,
      status: worker.status,
      sentAt: new Date(now - worker.sentMinutesAgo * 60_000).toISOString(),
      respondedAt: worker.response === 'pending' ? null : new Date(now - (worker.sentMinutesAgo - 5) * 60_000).toISOString(),
      openedAt: worker.response === 'pending' && worker.sentMinutesAgo > 20 ? null : new Date(now - (worker.sentMinutesAgo - 1) * 60_000).toISOString(),
      lastReminderAt: null,
      assignmentId: `${seed.id}-a${index + 1}`,
    })),
    etaMinutes: null,
  }
}

function buildTodayWorld(now: number) {
  const todayKey = toDateKey(now, MOCK_TIME_ZONE)
  const anchor = resolveAnchorMinutes(now)
  const workers: Worker[] = SEED_WORKERS.map(({ lastSeenMinutesAgo, ...worker }) => ({
    ...worker,
    lastSeenAt: new Date(now - lastSeenMinutesAgo * 60_000).toISOString(),
    locationSource: worker.lat == null ? null : worker.status === 'offline' ? 'last_known' : 'live',
  }))
  const jobs = SEED_JOBS.map((seed) => buildJob(seed, todayKey, anchor + seed.startOffset, now))
  jobs.push(buildJob(OVERDUE_JOB, addDaysToDateKey(todayKey, -14), 10 * 60, now))
  return { workers, jobs }
}

/** Static plan for days other than today (past days completed, future days scheduled). */
function buildPlanForDate(dateKey: string, todayKey: string, now: number): Job[] {
  const isPast = dateKey < todayKey
  const hours = [9, 10.5, 12, 13.5, 15, 16.5]
  return hours.map((hour, index) => {
    const seed = SEED_JOBS[index % SEED_JOBS.length]
    const staffed = isPast || index % 3 !== 2
    return buildJob(
      {
        ...seed,
        id: `${dateKey}-${index}`,
        bookingNo: `BK-${2000 + index}`,
        status: isPast ? 'completed' : staffed ? 'scheduled' : 'unassigned',
        requiredWorkers: 1,
        workers: staffed ? [accepted(SEED_WORKERS[index % 5].id, isPast ? 'completed' : 'assigned', 600)] : [],
      },
      dateKey,
      hour * 60,
      now,
    )
  })
}

const moveTowards = (from: { lat: number; lng: number }, to: { lat: number; lng: number }, fraction: number) => ({
  lat: from.lat + (to.lat - from.lat) * fraction,
  lng: from.lng + (to.lng - from.lng) * fraction,
})

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export function createMockRealtimeSource(): RealtimeDataSource {
  const world = buildTodayWorld(Date.now())
  const listeners = new Set<SocketHandlers>()
  const pendingTimers = new Set<ReturnType<typeof setTimeout>>()
  let tick = 0
  let createdJobs = 0
  let timer: ReturnType<typeof setInterval> | null = null

  const emit = (event: RealtimeEvent) => {
    listeners.forEach((listener) => listener.onEvent(event))
  }

  const later = (ms: number, fn: () => void) => {
    const id = setTimeout(() => {
      pendingTimers.delete(id)
      fn()
    }, ms)
    pendingTimers.add(id)
  }

  const findWorker = (id: string | null) => world.workers.find((worker) => worker.id === id)
  const findJob = (id: string | null) => world.jobs.find((job) => job.id === id)
  const findJobWorker = (job: Job | undefined, workerId: string) => job?.workers.find((worker) => worker.workerId === workerId)

  const failMaybe = () => {
    if (Math.random() < realtimeConfig.mockAssignFailureRate) {
      throw new Error('The server rejected this change (mock failure).')
    }
  }

  /** "Server" side: record a worker response and broadcast it. */
  const respond = (jobId: string, workerId: string, response: WorkerResponse, declineReason?: string) => {
    const job = findJob(jobId)
    const jobWorker = findJobWorker(job, workerId)
    if (!job || !jobWorker || jobWorker.response !== 'pending') return
    const at = new Date().toISOString()
    jobWorker.response = response
    jobWorker.respondedAt = at
    if (declineReason) jobWorker.declineReason = declineReason
    emit({ type: 'job.worker.response', jobId, workerId, response, at, declineReason: declineReason ?? undefined })

    if (response === 'accepted' && Date.parse(job.scheduledStart) - Date.now() < 90 * 60_000) {
      later(15_000, () => {
        const current = findJobWorker(findJob(jobId), workerId)
        if (!current || current.response !== 'accepted' || current.status !== 'assigned') return
        current.status = 'on_the_way'
        emit({ type: 'job.worker.status', jobId, workerId, status: 'on_the_way', at: new Date().toISOString() })
      })
    }
  }

  const step = () => {
    tick += 1
    const at = new Date().toISOString()
    const now = Date.now()

    // Server-side expiry of stale offers (response demo only).
    if (SHOW_WORKER_RESPONSE_STATUS) world.jobs.forEach((job) => {
      job.workers.forEach((jobWorker) => {
        if (jobWorker.response === 'pending' && now - Date.parse(jobWorker.sentAt) >= schedulingRules.expireAfterMinutes * 60_000) {
          respond(job.id, jobWorker.workerId, 'expired')
        }
      })
    })

    if (SHOW_WORKER_RESPONSE_STATUS && tick === DECLINE_DEMO_AFTER_TICKS) respond('j8', 'w6', 'declined', 'Unavailable')

    // A nearby candidate becomes busy, then free again (strip removes / re-adds the chip).
    if (tick === BUSY_DEMO_AFTER_TICKS || tick === BUSY_DEMO_AFTER_TICKS + BUSY_DEMO_DURATION_TICKS) {
      const worker = findWorker(BUSY_DEMO_WORKER_ID)
      if (worker) {
        worker.status = tick === BUSY_DEMO_AFTER_TICKS ? 'on_job' : 'available'
        emit({ type: 'worker.status', workerId: worker.id, status: worker.status, at })
      }
    }

    world.workers.forEach((worker) => {
      if (worker.lat == null || worker.lng == null) return
      const job = findJob(worker.currentJobId)
      const travelling = job && job.lat != null && job.lng != null && (job.status === 'en_route' || job.status === 'late')

      if (travelling && job.lat != null && job.lng != null) {
        const next = moveTowards({ lat: worker.lat, lng: worker.lng }, { lat: job.lat, lng: job.lng }, worker.status === 'late' ? 0.03 : 0.05)
        worker.lat = next.lat
        worker.lng = next.lng
        worker.lastSeenAt = at
        emit({ type: 'worker.location', workerId: worker.id, lat: next.lat, lng: next.lng, at })

        if (haversineKm(next, { lat: job.lat, lng: job.lng }) < ARRIVAL_KM) {
          job.status = 'in_progress'
          worker.status = 'on_job'
          const jobWorker = findJobWorker(job, worker.id)
          if (jobWorker) jobWorker.status = 'started'
          emit({ type: 'job.updated', job: { id: job.id, status: 'in_progress' } })
          emit({ type: 'job.worker.status', jobId: job.id, workerId: worker.id, status: 'started', at })
          emit({ type: 'worker.status.changed', workerId: worker.id, status: 'on_job', currentJobId: job.id, at })
        }
        return
      }

      // Small GPS drift for everyone else, every few seconds.
      if (worker.status !== 'offline' && tick % 6 === Number(worker.id.slice(1)) % 6) {
        worker.lat += (Math.random() - 0.5) * 0.0012
        worker.lng += (Math.random() - 0.5) * 0.0018
        worker.lastSeenAt = at
        emit({ type: 'worker.location', workerId: worker.id, lat: worker.lat, lng: worker.lng, at })
      }
    })

    if (tick % 45 === 0 && createdJobs < MAX_CREATED_JOBS) {
      const template = NEW_JOB_TEMPLATES[createdJobs % NEW_JOB_TEMPLATES.length]
      createdJobs += 1
      const startMs = Math.ceil((Date.now() + 90 * 60_000) / (15 * 60_000)) * 15 * 60_000
      const job: Job = {
        id: `jn${createdJobs}`,
        bookingNo: `BK-${1049 + createdJobs}`,
        service: template.service,
        categoryId: template.categoryId,
        categoryName: CATEGORY_NAME[template.categoryId] ?? null,
        area: template.area,
        lat: template.lat,
        lng: template.lng,
        address: null,
        scheduledStart: new Date(startMs).toISOString(),
        scheduledEnd: new Date(startMs + 60 * 60_000).toISOString(),
        status: 'unassigned',
        requiredWorkers: 1,
        workers: [],
        etaMinutes: null,
      }
      world.jobs.push(job)
      emit({ type: 'job.created', job: clone(job) })
    }
  }

  const ensureTimer = () => {
    if (timer || listeners.size === 0) return
    timer = setInterval(step, TICK_MS)
  }

  const stopTimerIfIdle = () => {
    if (listeners.size > 0) return
    if (timer) {
      clearInterval(timer)
      timer = null
    }
    pendingTimers.forEach((id) => clearTimeout(id))
    pendingTimers.clear()
  }

  return {
    kind: 'mock',

    fetchSnapshot: async ({ dateKey, signal }: FetchSnapshotParams): Promise<RealtimeSnapshot> => {
      const requestedAt = Date.now()
      await wait(350)
      if (signal?.aborted) throw createAbortError()
      const now = Date.now()
      const todayKey = toDateKey(now, MOCK_TIME_ZONE)
      const jobs = dateKey === todayKey
        ? world.jobs.filter((job) => toDateKey(job.scheduledStart, MOCK_TIME_ZONE) <= dateKey)
        : buildPlanForDate(dateKey, todayKey, now)
      return {
        workers: clone(world.workers),
        jobs: clone(jobs).map(applyGeocodeCache),
        generatedAt: new Date().toISOString(),
        timeZone: MOCK_TIME_ZONE,
        workHours: realtimeConfig.workHours,
        staffingAsOf: requestedAt,
      }
    },

    addJobWorker: async ({ job: { id: jobId }, workerId }) => {
      await wait(600)
      failMaybe()
      const job = findJob(jobId)
      if (!job || !findWorker(workerId)) throw new Error('Job or worker no longer exists.')
      if (findJobWorker(job, workerId)) throw new Error('This worker is already on the job.')
      const at = new Date().toISOString()
      const jobWorker: JobWorker = { workerId, response: 'pending', status: 'assigned', sentAt: at, assignmentId: `${jobId}-a${job.workers.length + 1}` }
      job.workers.push(jobWorker)
      if (job.status === 'unassigned') job.status = 'scheduled'
      // Server echo, then the worker's phone reacts.
      later(150, () => emit({ type: 'job.worker.added', jobId, worker: clone(jobWorker) }))
      later(4_000, () => {
        const current = findJobWorker(findJob(jobId), workerId)
        if (!current || current.response !== 'pending') return
        current.openedAt = new Date().toISOString()
        emit({ type: 'job.worker.added', jobId, worker: clone(current) })
      })
      later(8_000, () => (SHOW_WORKER_RESPONSE_STATUS && workerId === 'w5' ? respond(jobId, workerId, 'declined', 'Too far away') : respond(jobId, workerId, 'accepted')))
    },

    removeJobWorker: async ({ job: { id: jobId }, workerId, reason }) => {
      await wait(500)
      failMaybe()
      const job = findJob(jobId)
      if (!job || !findJobWorker(job, workerId)) throw new Error('This worker is no longer on the job.')
      job.workers = job.workers.filter((worker) => worker.workerId !== workerId)
      if (!job.workers.some((worker) => worker.response === 'pending' || worker.response === 'accepted') && job.status !== 'in_progress') {
        job.status = 'unassigned'
      }
      later(150, () => emit({ type: 'job.worker.removed', jobId, workerId, reason: reason ?? null }))
    },

    fetchCandidates: async ({ job, radiusKm }): Promise<JobCandidate[]> => {
      await wait(450)
      failMaybe()
      const current = findJob(job.id) ?? job
      return findNearbyCandidates(current, world.workers, world.jobs, { radiusKm }).map((candidate) => ({
        workerId: candidate.worker.id,
        distanceKm: candidate.distanceKm,
        etaMinutes: candidate.etaMinutes,
        hasConflict: candidate.hasConflict,
      }))
    },

    connect: (handlers): RealtimeConnection => {
      listeners.add(handlers)
      const openTimer = setTimeout(() => {
        handlers.onOpen()
        ensureTimer()
      }, 400)

      return {
        close: () => {
          clearTimeout(openTimer)
          listeners.delete(handlers)
          stopTimerIfIdle()
        },
        reconnectNow: () => {
          handlers.onOpen()
          ensureTimer()
        },
      }
    },
  }
}
